/**
 * O contexto de acesso a contas, persistido em `localStorage`.
 *
 * Ele responde a uma pergunta que o perfil sozinho não consegue responder depois de uma
 * troca de conta: **existe um dono com várias contas atrás da conta que estou vendo?**
 * A troca emite um token da subconta, e um `GET /users/me` feito com esse token devolve o
 * perfil da subconta — sem assinatura e com uma conta só. Sem uma marca fora do perfil, o
 * app esqueceria, a cada reload, que o seletor precisa continuar na tela.
 *
 * Os dois valores `agency*` são os originais e continuam gravados no navegador de quem já
 * usa o produto — por isso nada aqui os renomeia. Os `multi_account*` cobrem o assinante
 * **lojista** com mais de uma conta, um estado que os planos sempre permitiram
 * (`plans.max_accounts = 5` em toda a categoria `lojista`) e que a interface tratava como
 * exclusividade de agência: o seletor era gated por `plan_category === "agencia"`, então
 * quem tinha três contas num plano lojista via sempre a primeira, sem caminho para as
 * outras. É o defeito de **M-214**.
 */

import type {
  ProfileAccount,
  ProfileResponse,
} from "@/backend-sdk/client/types.gen";
import { normalizeAccessLevel, type AccessLevel } from "@/lib/access-level";

export const ACCOUNT_ACCESS_CONTEXT_KEY = "account_access_context";

export type AccountAccessContext =
  | "agency"
  | "agency_subaccount"
  | "multi_account"
  | "multi_account_subaccount"
  | "team_member";

export const getAccountAccessContext = (): string | null =>
  localStorage.getItem(ACCOUNT_ACCESS_CONTEXT_KEY);

export const setAccountAccessContext = (
  context: AccountAccessContext,
): void => {
  localStorage.setItem(ACCOUNT_ACCESS_CONTEXT_KEY, context);
};

export const clearAccountAccessContext = (): void => {
  localStorage.removeItem(ACCOUNT_ACCESS_CONTEXT_KEY);
};

/**
 * Agência — o que muda o **vocabulário** da tela ("Subcontas", "Gerenciar contas da
 * Agência") e o que a API cobra por plano. Não é o que decide se o seletor aparece.
 */
export const isAgencyAccessContext = (context: string | null): boolean =>
  context === "agency" || context === "agency_subaccount";

/**
 * Dono de mais de uma conta, agência ou não. É **este** predicado que decide se o seletor
 * de contas aparece.
 */
export const isMultiAccountAccessContext = (context: string | null): boolean =>
  isAgencyAccessContext(context) ||
  context === "multi_account" ||
  context === "multi_account_subaccount";

/**
 * O funcionário que navega pelas lojas de um time.
 *
 * Ele **não** entra em `isMultiAccountAccessContext`: os dois `multi_account*` significam
 * "há um dono com várias contas atrás desta tela", e o dono não é quem está logado. Ele
 * também não tem par "trocado", porque nunca troca de token — trocar de loja, para ele, é
 * trocar o `active_customer_uuid` que as telas mandam.
 */
export const ehContextoDeTime = (context: string | null): boolean =>
  context === "team_member";

/** Já trocou de conta: há um token de subconta em jogo. */
export const isSwitchedAccessContext = (context: string | null): boolean =>
  context === "agency_subaccount" || context === "multi_account_subaccount";

/** O par base/trocado da categoria do usuário, para gravar sem repetir a tabela. */
export const accountAccessContextFor = (opcoes: {
  isAgency: boolean;
  isSwitched: boolean;
}): AccountAccessContext => {
  if (opcoes.isAgency) {
    return opcoes.isSwitched ? "agency_subaccount" : "agency";
  }
  return opcoes.isSwitched ? "multi_account_subaccount" : "multi_account";
};

/** `plan_category` da assinatura → é agência? Aceita a grafia com e sem acento. */
export const ehPlanoDeAgencia = (
  planCategory: string | null | undefined,
): boolean => {
  const categoria = planCategory?.toLowerCase();
  return categoria === "agencia" || categoria === "agência";
};

/**
 * As contas do perfil são as que o usuário **possui**, e não apenas as que ele alcança?
 *
 * `GET /users/me` monta `accounts` de duas formas: para o assinante, são as contas dele e
 * cada uma vem com `status`; para o usuário convidado de uma conta, são os vínculos ativos
 * e cada um vem com `role`, com `status` nulo. Só o primeiro caso tem a **lista** de contas
 * para escolher: `GET /api/v1/accounts` responde `403 Usuário não é um assinante` para o
 * segundo, e oferecer o seletor a quem receberia 403 seria trocar um defeito por outro.
 *
 * <D-062> `POST /accounts/switch-account` saiu desta frase: ela deixou de exigir perfil de
 * assinante quando a conta virou o tenant, e hoje responde `200` a quem alcança a loja —
 * medido em 2026-09-06. Quem manda no seletor continua sendo a listagem.
 */
export const ehDonoDasContas = (
  contas: ProfileAccount[] | null | undefined,
): boolean => (contas ?? []).some((conta) => conta.status != null);

/**
 * O perfil em mãos é o de um **assinante** — alguém com cobrança própria.
 *
 * É o que decide se as abas **Faturas** e **Assinatura** da Minha Conta existem, e a
 * pergunta é a do servidor: `MyAccountContextService.assinanteDoUsuario` procura a linha em
 * `subscribers` do `sub` do token, e responde `404 Perfil de assinante não encontrado.` a
 * quem não a tem.
 *
 * <M-216> Quem responde é **`is_subscriber`**, o campo que `GET /users/me` passou a
 * publicar justamente para que este cliente não precisasse adivinhar. As três marcas abaixo
 * são o plano B, para o caso de a resposta vir de um servidor anterior ao campo — o campo é
 * `optional` no contrato pela régua de D-039/M-222, então o tipo gerado o traz como
 * `boolean | undefined`, e `undefined` aqui significa "não sei", não "não é".
 *
 * **E o plano B erra**, o que é a razão de o campo existir: ele reconhece o ramo do
 * assinante pelas marcas que o ramo do usuário de conta deixa nulas — `status` em alguma
 * conta, `subscription`, `plan_category` —, e nenhuma delas cobre o assinante **sem conta
 * nenhuma e sem assinatura `ACTIVE`/`TRIAL`**. Esse estado é alcançável: `DELETE
 * /api/v1/accounts/{id}` não guarda a última loja, e a criação de conta do `POST /register`
 * é best-effort. Para ele, `/my-account/invoices` responde `200` e as marcas dizem `false`.
 */
export const ehAssinante = (
  perfil: ProfileResponse | null | undefined,
): boolean => {
  if (!perfil) return false;
  if (perfil.is_subscriber !== undefined) return perfil.is_subscriber;
  return (
    ehDonoDasContas(perfil.accounts) ||
    perfil.subscription != null ||
    perfil.plan_category != null
  );
};

/**
 * O assinante está **sem plano ativo**: tem cobrança própria, mas o perfil não traz
 * assinatura `ACTIVE`/`TRIAL` (`GET /users/me` filtra por esses dois status).
 *
 * Cobre duas pessoas diferentes: quem se cadastrou e saiu antes do checkout — `POST
 * /register` cria o usuário, o perfil de assinante e a loja `INACTIVE`, e só `POST
 * /auth/checkout-data` cria a assinatura — e quem teve a assinatura **suspensa** (trial
 * vencido, fatura em atraso). Por isso esta função é só o **pré-filtro** de
 * `ExigePlanoAtivo`, que pergunta ao servidor qual das duas é antes de desviar alguém para a
 * escolha do plano. Perfil ainda carregando é "não sei", e não responde `true`.
 */
export const semPlanoAtivo = (
  perfil: ProfileResponse | null | undefined,
): boolean =>
  perfil != null && ehAssinante(perfil) && perfil.subscription == null;

/**
 * Quem está olhando administra a equipe da conta em que opera?
 *
 * É o que decide se a aba **Usuários** da Minha Conta lista gente ou explica por que não
 * lista. A pergunta é do servidor — `MyAccountUsersService.administraAConta` —, e ela tem
 * duas trilhas (vínculo `admin` na conta, **ou** ser o assinante dono dela) sobre uma conta
 * que sai do **token**, resolvida como o vínculo ativo mais antigo. Ou seja: nem o papel
 * publicado no perfil, nem a conta do seletor bastariam para reconstruí-la aqui.
 *
 * <M-247> Por isso quem responde é **`can_manage_account_users`**, publicado por
 * `GET /users/me` justamente para este uso. Ele é `optional` no contrato pela régua de
 * D-039/M-222, então o tipo gerado o traz como `boolean | undefined` — e `undefined`
 * significa "resposta de um servidor anterior ao campo", não "não administra".
 *
 * **O plano B é chamar a rota**, e não deduzir. Sem o campo, `MyAccountPage` volta a pedir
 * a listagem e trata o `403` como a resposta: é mais uma requisição, mas é a única
 * definição do guard que existe fora do servidor. Deduzir de `accounts[].access_level`
 * seria escrever uma segunda, que erraria no assinante (cujo `access_level` sai `null`) e
 * em quem alcança mais de uma conta.
 */
export const administraAConta = (
  perfil: ProfileResponse | null | undefined,
): boolean | undefined => perfil?.can_manage_account_users;

/**
 * O acesso da pessoa em cada conta que ela alcança, para a tela dizer a ela o que ela é.
 *
 * Lê `access_level`, e **não** `role`. Os dois campos existem na resposta e divergem em
 * dado real: <D-061> aposentou o eixo `roles`, que continua gravado em espelho porque a
 * coluna é `NOT NULL` e nunca mais autoriza nada — no banco de teste,
 * `bruno@agencia.seed.example` é "Visualizador" em `role` e `editor` em `access_level`.
 * Dizer a ele "seu acesso: Visualizador" seria mentir com um campo publicado.
 *
 * Sai vazio para o assinante: lá as contas são as que ele **possui**, sem vínculo de que
 * tirar nível — e ele administra a equipe de qualquer jeito, então a lista não seria lida.
 */
export const acessosDoPerfil = (
  perfil: ProfileResponse | null | undefined,
): { accountId: string; accountName: string; level: AccessLevel }[] =>
  // `flatMap` e não `filter` + `map`: o `filter` não estreita o tipo, e fechar a diferença
  // com um `as` seria esconder do compilador exatamente o campo que este predicado existe
  // para ler (a régua de D-049).
  (perfil?.accounts ?? []).flatMap((conta) =>
    conta.access_level == null
      ? []
      : [
          {
            accountId: conta.id,
            accountName: conta.name,
            level: normalizeAccessLevel(conta.access_level),
          },
        ],
  );

/**
 * A conta que vale como ativa enquanto o dono não escolher outra.
 *
 * Agência tem "Conta Principal", um lugar legítimo em que ficar sem conta nenhuma
 * selecionada. O lojista com várias contas não tem: toda tela dele lê
 * `active_customer_uuid` como tenant, e algumas quebram sem ele. Então há sempre um
 * padrão — a última conta escolhida, se ela ainda existe, ou a primeira da lista.
 */
export const contaPadraoDoDono = (
  contas: ProfileAccount[] | null | undefined,
  ultimaEscolhidaId: string | null,
): ProfileAccount | null => {
  const lista = contas ?? [];
  return (
    lista.find((conta) => conta.id === ultimaEscolhidaId) ?? lista[0] ?? null
  );
};
